import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import type { CalendarEventFormDataV2 } from "@/types/calendar"
import { useEventFormLogic } from "@/hooks/use-event-form-logic"
import { useMainSignature } from "@/hooks/use-main-signature"
import {
  useGetCandidatesCandidateId,
  useGetCandidatesCandidateIdJobs,
  useGetCandidatesSearch,
  useGetContacts,
  useGetEventsProvider,
  useGetJobs,
  useGetJobsJobIdCandidate,
  useGetJobsJobId,
} from "openapi/api/endpoints"
import { useCalendarBooking } from "@/hooks/use-calendar-booking"
import { appendSignatureIfMissing, requiresInviteMessage, supportsCustomInviteMessage } from "@/lib/calendar-invite-message"
import { asArray } from "@/lib/as-array"
import { formatBusySlotTime } from "@/lib/calendar-busy-slots"
import { createCalendarEventFormDefaults } from "@/lib/calendar-event-defaults"
import { getCalendarEventPresetContext, mergePresetJobOption } from "@/lib/calendar-event-presets"
import { useCalendarEventSubmit } from "@/hooks/use-calendar-event-submit"
import type { Event } from "openapi/api/model"

type UseCalendarEventDialogControllerArgs = {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultValues?: Partial<CalendarEventFormDataV2>
  mode: "create" | "edit"
  selectedEvent?: Event | null
}

const toMinutes = (time?: string) => {
  if (!time) return null
  const [hours, minutes] = time.split(":").map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  return hours * 60 + minutes
}

export function useCalendarEventDialogController({
  open,
  onOpenChange,
  defaultValues,
  mode,
  selectedEvent,
}: UseCalendarEventDialogControllerArgs) {
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { mainSignature } = useMainSignature()
  const { data: eventsProvider } = useGetEventsProvider()
  const supportsInviteMessage = supportsCustomInviteMessage(eventsProvider)
  const { submitEvent, deleteEvent, allowAttachments } = useCalendarEventSubmit()

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
  } = useForm<CalendarEventFormDataV2>({
    defaultValues: createCalendarEventFormDefaults(defaultValues),
  })

  const selectedDate = watch("date")
  const startTime = watch("start_time")
  const endTime = watch("end_time")
  const linkType = watch("linkType")
  const selectedJob = watch("job_id")
  const selectedCandidate = watch("candidate_id")
  const sendInvites = watch("send_invites")
  const includeInviteMessage = watch("include_invite_message")
  const inviteeEmails = watch("invitee_emails") ?? []
  const newAttachments = watch("attachments") ?? []
  const existingAttachments = watch("existing_attachments") ?? []
  const removeAttachmentIds = watch("remove_attachment_ids") ?? []
  const eventType = watch("event_type")

  const { presetCandidateId, isPresetCandidate, presetJobId, isPresetJob } = getCalendarEventPresetContext(
    mode,
    defaultValues,
  )

  const { data: jobs } = useGetJobs()
  const { data: presetJob } = useGetJobsJobId(presetJobId, { query: { enabled: isPresetJob } })
  const { data: candidates } = useGetCandidatesSearch({ job_id: selectedJob }, { query: { enabled: !!selectedJob } })
  const resolvedSelectedCandidateId = selectedCandidate || presetCandidateId || ""
  const { data: selectedJobCandidateProfile } = useGetJobsJobIdCandidate(
    selectedJob || presetJobId,
    { candidate_id: resolvedSelectedCandidateId },
    { query: { enabled: linkType === "candidate" && !!(selectedJob || presetJobId) && !!resolvedSelectedCandidateId } },
  )
  const { data: selectedCandidateProfile } = useGetCandidatesCandidateId(
    resolvedSelectedCandidateId,
    { query: { enabled: linkType === "candidate" && !(selectedJob || presetJobId) && !!resolvedSelectedCandidateId } },
  )
  const { data: candidateJobs } = useGetCandidatesCandidateIdJobs(
    presetCandidateId,
    { query: { enabled: isPresetCandidate } },
  )
  const { data: candidateContactInfo } = useGetContacts(
    { candidateId: (selectedCandidate || presetCandidateId || "") },
    { query: { enabled: mode === "create" && linkType === "candidate" && !!(selectedCandidate || presetCandidateId) } },
  )

  const candidateEmail =
    candidateContactInfo?.[0]?.email && candidateContactInfo?.[0]?.email !== "enrich_layer_not_found"
      ? candidateContactInfo[0].email
      : ""

  /*
    `?? []` only guards null. Both of these come straight off an endpoint, and
    an error body or an envelope reaches `.filter` as an object — which throws
    and takes the whole dialog down rather than leaving one picker empty.
  */
  const candidateJobOptions = asArray<NonNullable<typeof candidateJobs>[number]>(candidateJobs)
    .filter((job): job is typeof job & { id: string } => Boolean(job.id))
    .map((job) => ({
      id: job.id,
      title: job.title,
      company_name: job.company_name,
    }))

  const baseJobOptions = isPresetCandidate
    ? candidateJobOptions
    : jobs

  const presetJobOption =
    isPresetJob && presetJob?.id
      ? {
          id: presetJob.id,
          title: presetJob.title,
          company_name: presetJob.company_name,
        }
      : undefined

  const jobOptions = mergePresetJobOption({ presetJob: presetJobOption, baseJobOptions })
  const selectedCandidateOption = resolvedSelectedCandidateId
    ? {
        id: resolvedSelectedCandidateId,
        full_name: selectedJobCandidateProfile?.full_name ?? selectedCandidateProfile?.full_name ?? "Candidate",
      }
    : undefined
  const normalizedCandidateOptionsFromSearch = asArray<NonNullable<typeof candidates>[number]>(candidates).map((candidate) => ({
    id: candidate.candidates?.id ?? "",
    full_name: candidate.candidates?.full_name ?? "Candidate",
    stage: candidate.stage,
  }))
  const normalizedCandidateOptions = selectedCandidateOption
    ? [
        selectedCandidateOption,
        ...normalizedCandidateOptionsFromSearch.filter((candidate) => candidate.id !== resolvedSelectedCandidateId),
      ]
    : normalizedCandidateOptionsFromSearch

  const { overlappingBusySlots, isBusySlotsPending } = useCalendarBooking({
    selectedDate,
    startTime,
    endTime,
    mode,
    currentProviderEventId: selectedEvent?.provider_event_id ?? undefined,
  })

  const {
    inviteeDraft,
    setInviteeDraft,
    handleInviteeDraftKeyDown,
    handleInviteeDraftBlur,
    removeInviteeEmail,
    handleStartTimeChange,
    handleEndTimeChange,
  } = useEventFormLogic({
    open,
    mode,
    selectedDate,
    startTime,
    endTime,
    candidateEmail,
    shouldAutoInjectCandidateEmail:
      mode === "create" && linkType === "candidate" && !!(selectedCandidate || presetCandidateId),
    shouldAutoAdjustEndTime: true,
    inviteeEmails,
    getValues,
    setValue,
  })

  useEffect(() => {
    if (!open) {
      setShowDeleteConfirm(false)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      reset(createCalendarEventFormDefaults(defaultValues))
    }
  }, [open, defaultValues, reset])

  useEffect(() => {
    if (!open || !supportsInviteMessage || !includeInviteMessage) return
    const signature = (mainSignature ?? "").trim()
    if (!signature) return
    setValue("invite_message", appendSignatureIfMissing(getValues("invite_message"), signature), { shouldDirty: false })
  }, [open, supportsInviteMessage, includeInviteMessage, mainSignature, getValues, setValue])

  const handleFormSubmit = async (data: CalendarEventFormDataV2) => {
    if (isBusySlotsPending) return

    if (mode === "create") {
      if (!data.job_id) {
        alert("Please select a job to link this event to.")
        return
      }
      if (data.linkType === "candidate" && !data.candidate_id) {
        alert("Please select a candidate to link this event to.")
        return
      }
    }

    if (mode === "create" && data.send_invites && (data.invitee_emails ?? []).length === 0) {
      alert("Please add at least one invitee email.")
      return
    }

    if (mode === "create" && requiresInviteMessage({
      sendInvites: data.send_invites,
      supportsInviteMessage,
      includeInviteMessage: data.include_invite_message,
      inviteMessage: data.invite_message,
    })) {
      alert("Enter an invite message or disable the custom invite message option.")
      return
    }

    const startMinutes = toMinutes(data.start_time)
    const endMinutes = toMinutes(data.end_time)
    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      alert("End time must be after the start time.")
      return
    }

    if (overlappingBusySlots.length > 0) {
      const slotSummary = overlappingBusySlots
        .slice(0, 3)
        .map((slot) => {
          const eventName = slot.eventName?.trim() || "Busy"
          return `${formatBusySlotTime(slot.start)} - ${formatBusySlotTime(slot.end)} (${eventName})`
        })
        .join("\n")
      const moreCount = overlappingBusySlots.length > 3 ? `\n...and ${overlappingBusySlots.length - 3} more` : ""

      const confirmed = window.confirm(
        `Selected time overlaps with ${overlappingBusySlots.length} busy slot(s):\n${slotSummary}${moreCount}\n\nDo you want to continue?`
      )
      if (!confirmed) return
    }

    try {
      setIsSubmitting(true)
      await submitEvent({
        mode,
        data,
        selectedEvent,
      })
      reset()
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedEvent?.id) return
    await deleteEvent(selectedEvent.id)
    onOpenChange(false)
  }

  return {
    register,
    handleSubmit,
    setValue,
    selectedDate,
    startTime,
    endTime,
    linkType,
    selectedJob,
    selectedCandidate,
    isPresetCandidate,
    isPresetJob,
    sendInvites,
    includeInviteMessage,
    supportsInviteMessage,
    allowAttachments,
    inviteeEmails,
    newAttachments,
    existingAttachments,
    removeAttachmentIds,
    eventType,
    jobs: jobOptions,
    candidates: normalizedCandidateOptions,
    overlappingBusySlots,
    inviteeDraft,
    setInviteeDraft,
    handleInviteeDraftKeyDown,
    handleInviteeDraftBlur,
    removeInviteeEmail,
    handleStartTimeChange,
    handleEndTimeChange,
    datePickerOpen,
    setDatePickerOpen,
    showDeleteConfirm,
    setShowDeleteConfirm,
    isSubmitting,
    isBusySlotsPending,
    handleFormSubmit,
    handleDelete,
  }
}
